import type { IWebSocketConnection } from './types'
import { WebSocketConnection } from './ws'

export type ConnectionName = 'public' | 'private'

type TokenProvider = () => string | Promise<string>

type WsManagerOptions = {
  baseUrl: string
  getPrivateToken?: TokenProvider
}

export class WebSocketConnectionManager {
  private connections = new Map<ConnectionName, IWebSocketConnection>()
  private pendingConnections = new Map<ConnectionName, Promise<IWebSocketConnection>>()

  constructor(private options: WsManagerOptions) {
    this.options = {
      ...options,
      baseUrl: options.baseUrl.replace(/\/+$/, '')
    }
  }

  async getConnection(name: ConnectionName): Promise<IWebSocketConnection> {
    if (typeof window === 'undefined') {
      throw new Error(`WebSocket connection "${name}" is not available during SSR`)
    }

    const existing = this.connections.get(name)
    if (existing) return existing

    const pending = this.pendingConnections.get(name)
    if (pending) return pending

    const promise = this.createConnection(name).finally(() => {
      this.pendingConnections.delete(name)
    })

    this.pendingConnections.set(name, promise)

    return promise
  }

  private async createConnection(name: ConnectionName): Promise<IWebSocketConnection> {
    if (name === 'public') {
      const connection = new WebSocketConnection(
        `${this.options.baseUrl}/market`,
        { reconnectInterval: 60_000 }
      )

      await connection.connect()
      this.connections.set(name, connection)

      return connection
    }

    const token = await this.getPrivateToken()

    const connection = new WebSocketConnection(
      `${this.options.baseUrl}/private`,
      { reconnectInterval: 10_000 },
      {
        requiredAuth: true,
        token,
      }
    )

    await connection.connect()
    await connection.auth()

    this.connections.set(name, connection)

    return connection
  }

  private async getPrivateToken(): Promise<string> {
    if (!this.options.getPrivateToken) {
      throw new Error('Private WS token provider is not configured')
    }

    return this.options.getPrivateToken()
  }

  close(name?: ConnectionName) {
    if (name) {
      this.connections.get(name)?.close()
      this.connections.delete(name)
      return
    }

    this.connections.forEach((connection) => connection.close())
    this.connections.clear()
  }
}
